import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SEOFields from "./SEOFields";

const empty = { title: "", excerpt: "", content: "", cover_image: "", section: "wealth", external_link: "", seo: {} };

function ArticleForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || empty);
  const set = (k, v) => setF({ ...f, [k]: v });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, external_link: f.external_link || null, seo: f.seo || {} }); }} className="space-y-4">
      <div><Label>Section</Label>
        <Select value={f.section} onValueChange={(v) => set("section", v)}>
          <SelectTrigger data-testid="article-form-section"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="wealth">Healthy Guides</SelectItem><SelectItem value="kitchen">Kitchen Corner</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label>Titre</Label><Input value={f.title} onChange={e => set("title", e.target.value)} required data-testid="article-form-title" /></div>
      <div><Label>Résumé</Label><Textarea value={f.excerpt} onChange={e => set("excerpt", e.target.value)} required rows={2} data-testid="article-form-excerpt" /></div>
      <div><Label>Contenu</Label><Textarea value={f.content} onChange={e => set("content", e.target.value)} required rows={8} data-testid="article-form-content" /></div>
      <div><Label>Image de couverture (URL)</Label><Input value={f.cover_image} onChange={e => set("cover_image", e.target.value)} required data-testid="article-form-cover" /></div>
      <div><Label>Lien externe (optionnel)</Label><Input value={f.external_link || ""} onChange={e => set("external_link", e.target.value)} placeholder="https://..." data-testid="article-form-external" /></div>
      <details className="border border-[#E5EAE5] rounded-xl">
        <summary className="cursor-pointer px-4 py-3 font-semibold text-sm" data-testid="article-form-seo-toggle">SEO de l'article (avancé)</summary>
        <div className="p-4 border-t border-[#E5EAE5]">
          <SEOFields value={f.seo || {}} onChange={(seo) => set("seo", seo)} testIdPrefix="article-form-seo" />
        </div>
      </details>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
        <Button type="submit" className="bg-[#7AC143] hover:bg-[#67BC2A]" data-testid="article-form-submit">Publier</Button>
      </div>
    </form>
  );
}

export default function ArticleEditor() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    const r = await api.get("/articles");
    setItems(r.data);
  }, []);
  useEffect(() => { refresh().catch(() => {}); }, [refresh]);

  const save = async (f) => {
    try {
      if (editing) await api.put(`/articles/${editing.id}`, f);
      else await api.post("/articles", f);
      toast.success("Article enregistré");
      setOpen(false); setEditing(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/articles/${id}`);
    toast.success("Supprimé");
    refresh();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#7AC143] hover:bg-[#67BC2A]" data-testid="article-add-btn"><Plus className="w-4 h-4 mr-2" /> Nouvel article</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouvel"} article</DialogTitle></DialogHeader>
            <ArticleForm initial={editing} onSubmit={save} onCancel={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E5EAE5] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Titre</TableHead><TableHead>Section</TableHead><TableHead>Auteur</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(a => (
              <TableRow key={a.id} data-testid={`article-row-${a.id}`}>
                <TableCell><div className="flex items-center gap-3"><img src={a.cover_image} className="w-12 h-12 rounded-lg object-cover" alt="" /><span className="font-semibold">{a.title}</span></div></TableCell>
                <TableCell><Badge variant="outline">{a.section === "wealth" ? "Healthy Guides" : "Kitchen Corner"}</Badge></TableCell>
                <TableCell className="text-sm">{a.author}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setOpen(true); }} data-testid={`article-edit-${a.id}`}><Edit2 className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(a.id)} data-testid={`article-delete-${a.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
